import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Checkbox,
  Chip,
  Typography,
  Box,
  Collapse,
  Button,
  Stack,
  FormControlLabel,
} from '@mui/material';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { config } from '../../config';

interface Category {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Service {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  category_id?: number;
  created_at: string;
  updated_at: string;
}

interface CategoriesTableProps {
  categories: Category[];
  onSave: (id: number, data: Partial<Category>) => Promise<void>;
  loading?: boolean;
  onSaveService?: (id: string, data: Partial<Service>) => Promise<void>;
}

const CategoriesTable: React.FC<CategoriesTableProps> = ({
  categories,
  onSave,
  loading = false,
  onSaveService,
}) => {
  const { t } = useLanguage();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<Category>>({});
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  const [editServiceForm, setEditServiceForm] = useState<Partial<Service>>({});
  const [showAddServiceForm, setShowAddServiceForm] = useState(false);
  const [newServiceForm, setNewServiceForm] = useState({
    name: '',
    description: '',
    is_active: true
  });
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [servicesData, setServicesData] = useState<Record<number, Service[]>>({});
  const [loadingServices, setLoadingServices] = useState<Set<number>>(new Set());

  const handleEdit = (category: Category) => {
    setEditingId(category.id);
    setEditForm({
      name: category.name,
      description: category.description,
      is_active: category.is_active,
    });
  };

  const handleSave = async () => {
    if (!editingId || !editForm.name) return;

    try {
      await onSave(editingId, editForm);
      setEditingId(null);
      setEditForm({});
    } catch (error) {
      console.error('Failed to save category:', error);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleInputChange = (field: keyof Category, value: string | boolean) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditService = (service: Service) => {
    setEditingServiceId(service.id);
    setEditServiceForm({
      name: service.name,
      description: service.description,
      is_active: service.is_active,
    });
  };

  const handleSaveService = async () => {
    if (!editingServiceId || !editServiceForm.name || !onSaveService) return;

    try {
      await onSaveService(editingServiceId, editServiceForm);

      // Find which category this service belongs to and refresh its services
      const categoryId = Object.keys(servicesData).find(catId => {
        const services = servicesData[parseInt(catId)];
        return services?.some(service => service.id === editingServiceId);
      });

      if (categoryId) {
        await loadServicesForCategory(parseInt(categoryId));
      }

      setEditingServiceId(null);
      setEditServiceForm({});
    } catch (error) {
      console.error('Failed to save service:', error);
    }
  };

  const handleCancelService = () => {
    setEditingServiceId(null);
    setEditServiceForm({});
  };

  const handleServiceInputChange = (field: keyof Service, value: string | boolean) => {
    setEditServiceForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleToggleExpand = async (categoryId: number) => {
    const newExpandedRows = new Set(expandedRows);

    if (expandedRows.has(categoryId)) {
      // Collapse
      newExpandedRows.delete(categoryId);
      setExpandedRows(newExpandedRows);
    } else {
      // Expand
      newExpandedRows.add(categoryId);
      setExpandedRows(newExpandedRows);

      // Load services if not already loaded
      if (!servicesData[categoryId]) {
        await loadServicesForCategory(categoryId);
      }
    }
  };

  const loadServicesForCategory = async (categoryId: number) => {
    setLoadingServices(prev => new Set(prev).add(categoryId));

    try {
      // Using a simple fetch with auth header since we don't have direct access to apiCall
      const token = localStorage.getItem(config.auth.tokenKey);
      const response = await fetch(`/api/v1/services?category_id=${categoryId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(t('errors.loadServicesFailed', 'categories'));
      }
      const data = await response.json();

      // Filter services to only show those belonging to this category (backend should already filter, but as backup)
      const filteredServices = (data.services || []).filter(
        (service: Service) => service.category_id === categoryId
      );

      setServicesData(prev => ({
        ...prev,
        [categoryId]: filteredServices
      }));
    } catch (error) {
      console.error('Failed to load services:', error);
    } finally {
      setLoadingServices(prev => {
        const newSet = new Set(prev);
        newSet.delete(categoryId);
        return newSet;
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography>{t('table.loading', 'categories')}</Typography>
      </Box>
    );
  }

  if (categories.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography variant="body1" color="text.secondary">
          {t('table.noData', 'categories')}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" sx={{ fontWeight: 'bold' }}></TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('table.headers.id', 'categories')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('table.headers.name', 'categories')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('table.headers.description', 'categories')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('table.headers.active', 'categories')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('table.headers.created', 'categories')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('table.headers.actions', 'categories')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {categories.map((category) => {
            const isExpanded = expandedRows.has(category.id);
            const services = servicesData[category.id] || [];
            const isLoadingServices = loadingServices.has(category.id);

            return (
              <React.Fragment key={category.id}>
                <TableRow
                  hover
                  sx={{
                    '& .MuiTableCell-root': { color: 'primary.main' },
                    cursor: editingId !== category.id ? 'pointer' : 'default'
                  }}
                  onClick={() => {
                    if (editingId !== category.id) {
                      handleToggleExpand(category.id);
                    }
                  }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleExpand(category.id)}
                      disabled={editingId === category.id}
                    >
                      {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                  </TableCell>

                  {editingId === category.id ? (
                    <>
                      <TableCell>{category.id}</TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={editForm.name || ''}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          required
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={3}
                          value={editForm.description || ''}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={editForm.is_active || false}
                          onChange={(e) => handleInputChange('is_active', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(category.created_at).toLocaleDateString('uk-UA')}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleSave}
                            disabled={!editForm.name}
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={handleCancel}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{category.id}</TableCell>
                      <TableCell>{category.name}</TableCell>
                      <TableCell>
                        {category.description || (
                          <Typography variant="body2" color="text.secondary">
                            {t('table.noDescription', 'categories')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={category.is_active ? t('table.status.yes', 'categories') : t('table.status.no', 'categories')}
                          color={category.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(category.created_at).toLocaleDateString('uk-UA')}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(category)}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </>
                  )}
                </TableRow>

                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h6" component="div">
                            {t('services.title', 'categories')}
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => setShowAddServiceForm(true)}
                            disabled={showAddServiceForm}
                          >
                            {t('services.addButton', 'categories')}
                          </Button>
                        </Box>

                        {showAddServiceForm && (
                          <Paper sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                              {t('add.title', 'categories').replace('категорію', 'послугу').replace('Category', 'Service')}
                            </Typography>

                            <Stack spacing={2}>
                              <TextField
                                fullWidth
                                size="small"
                                label={t('services.headers.name', 'categories')}
                                placeholder={t('add.namePlaceholder', 'categories').replace('категорії', 'послуги').replace('category', 'service')}
                                value={newServiceForm.name}
                                onChange={(e) => setNewServiceForm(prev => ({ ...prev, name: e.target.value }))}
                                required
                              />

                              <TextField
                                fullWidth
                                size="small"
                                label={t('services.headers.description', 'categories')}
                                placeholder={t('add.descriptionPlaceholder', 'categories').replace('категорії', 'послуги').replace('category', 'service')}
                                multiline
                                rows={2}
                                value={newServiceForm.description}
                                onChange={(e) => setNewServiceForm(prev => ({ ...prev, description: e.target.value }))}
                              />

                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={newServiceForm.is_active}
                                    onChange={(e) => setNewServiceForm(prev => ({ ...prev, is_active: e.target.checked }))}
                                  />
                                }
                                label={t('services.headers.active', 'categories')}
                              />

                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<CloseIcon />}
                                  onClick={() => {
                                    setNewServiceForm({ name: '', description: '', is_active: true });
                                    setShowAddServiceForm(false);
                                  }}
                                >
                                  {t('add.cancel', 'categories')}
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<CheckIcon />}
                                  onClick={async () => {
                                    if (!newServiceForm.name.trim()) return;

                                    try {
                                      if (onSaveService) {
                                        await onSaveService('', {
                                          ...newServiceForm,
                                          category_id: category.id
                                        });
                                        await loadServicesForCategory(category.id);
                                        setNewServiceForm({ name: '', description: '', is_active: true });
                                        setShowAddServiceForm(false);
                                      }
                                    } catch (error) {
                                      console.error('Failed to add service:', error);
                                    }
                                  }}
                                  disabled={!newServiceForm.name.trim()}
                                >
                                  {t('add.submit', 'categories')}
                                </Button>
                              </Box>
                            </Stack>
                          </Paper>
                        )}

                        {isLoadingServices ? (
                          <Box display="flex" justifyContent="center" p={2}>
                            <Typography>{t('services.loading', 'categories')}</Typography>
                          </Box>
                        ) : services.length === 0 ? (
                          <Box display="flex" justifyContent="center" p={2}>
                            <Typography variant="body2" color="text.secondary">
                              {t('services.noServices', 'categories')}
                            </Typography>
                          </Box>
                        ) : (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('services.headers.name', 'categories')}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('services.headers.description', 'categories')}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('services.headers.active', 'categories')}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('table.headers.actions', 'categories')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {services.map((service) => (
                                <TableRow key={service.id}>
                                  {editingServiceId === service.id ? (
                                    <>
                                      <TableCell>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          value={editServiceForm.name || ''}
                                          onChange={(e) => handleServiceInputChange('name', e.target.value)}
                                          required
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          multiline
                                          rows={2}
                                          value={editServiceForm.description || ''}
                                          onChange={(e) => handleServiceInputChange('description', e.target.value)}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Checkbox
                                          checked={editServiceForm.is_active || false}
                                          onChange={(e) => handleServiceInputChange('is_active', e.target.checked)}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Box display="flex" gap={1}>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={handleSaveService}
                                            disabled={!editServiceForm.name}
                                          >
                                            <CheckIcon />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            color="secondary"
                                            onClick={handleCancelService}
                                          >
                                            <CloseIcon />
                                          </IconButton>
                                        </Box>
                                      </TableCell>
                                    </>
                                  ) : (
                                    <>
                                      <TableCell>{service.name}</TableCell>
                                      <TableCell>
                                        {service.description || (
                                          <Typography variant="body2" color="text.secondary">
                                            {t('table.noDescription', 'categories')}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={service.is_active ? t('table.status.yes', 'categories') : t('table.status.no', 'categories')}
                                          color={service.is_active ? 'success' : 'default'}
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => handleEditService(service)}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                      </TableCell>
                                    </>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CategoriesTable;
